import { useEffect, type ErrorInfo } from 'react';
import {
    Admin,
    CustomRoutes,
    ListGuesser,
    RaThemeOptions,
    Resource,
    defaultTheme,
    localStorageStore,
} from 'react-admin';
import type { AdminProps, AuthProvider, DataProvider } from 'react-admin';
import { Typography, Button, Box } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { Route, Navigate } from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import { ForgotPasswordPage, SetPasswordPage } from 'ra-supabase';

import { Layout } from '../pages/layout/Layout';
import { i18nProvider } from './i18nProvider';
import { MarketsView } from '../pages/markets/MarketsView';
import { TraderView } from '../pages/markets/views/TraderView';
import { PortfolioView } from '../pages/portfolio/Portfolio';
import { MonitorView } from '../pages/monitor/MonitorView';
import { LoginPage } from '../pages/public/LoginPage';
import { SignupPage } from '../pages/public/SignupPage';
import { ChangeTempPasswordPage } from '../pages/public/ChangeTempPasswordPage';
import {
    authProvider as defaultAuthProvider,
    dataProvider as defaultDataProvider,
} from '../providers/firebase';

import { SettingsPage } from '../pages/settings/SettingsPage';
import { SettingsNotifications } from '../notifications/SettingsNotifications';
import {
    ConfigurationContextValue,
    ConfigurationProvider,
} from './ConfigurationContext';

import { lightTheme, darkTheme } from './theme'; // Import themes

import funds from '../pages/funds';
import transactions from '../pages/transactions';
import insights from '../pages/insights';
import users from '../pages/users';
import {
    UsersList as AdminUsers,
    SubscribersList,
    AuditList,
} from '../pages/admin';
import { UsersEdit } from '../pages/users/UserEdit';
import { ConversationView } from '../pages/markets/views/ConversationView';
import { ConversationListView } from '../pages/markets/views/ConversationListView';
import { NewConversationView } from '../pages/markets/views/NewConversationView';
import { WatchlistView } from '../pages/markets/components/WatchlistView';
import { ConvictionScreenerView } from '../pages/markets/components/ConvictionScreenerView';
import { SimulationView } from '../pages/markets/components/SimulationView';
import { GatesView } from '../pages/gates/GatesView';
import { RequireAccess } from '../auth/RequireAccess';
import { DefaultLanding } from '../auth/DefaultLanding';
import NoAccessPage from '../pages/misc/NoAccessPage';

const AdminErrorFallback = ({
    error,
    resetErrorBoundary,
}: {
    error: Error;
    errorInfo?: ErrorInfo;
    resetErrorBoundary: (...args: any[]) => void;
}) => (
    <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
            Something went wrong
        </Typography>
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, whiteSpace: 'pre-wrap' }}
        >
            {error?.message || String(error)}
        </Typography>
        <Button variant="contained" onClick={resetErrorBoundary}>
            Try again
        </Button>
    </Box>
);

// Define the interface for the IRM component props
export type IRMProps = {
    dataProvider?: DataProvider;
    authProvider?: AuthProvider;
    lightTheme?: RaThemeOptions;
    darkTheme?: RaThemeOptions;
} & Partial<ConfigurationContextValue> &
    Partial<AdminProps>;

export const IRM = ({
    dataProvider = defaultDataProvider,
    authProvider = defaultAuthProvider,
    disableTelemetry,
    ...rest
}: IRMProps) => {
    return (
        <ConfigurationProvider>
            <BrowserRouter>
                <Admin
                    dataProvider={dataProvider}
                    authProvider={authProvider}
                    store={localStorageStore(undefined, 'IRM')}
                    layout={Layout}
                    loginPage={LoginPage}
                    dashboard={DefaultLanding}
                    theme={lightTheme}
                    darkTheme={darkTheme}
                    i18nProvider={i18nProvider}
                    error={AdminErrorFallback}
                    requireAuth
                    disableTelemetry
                    {...rest}
                >
                    <CustomRoutes noLayout>
                        <Route
                            path={SignupPage.path}
                            element={<SignupPage />}
                        />
                        <Route
                            path={SetPasswordPage.path}
                            element={<SetPasswordPage />}
                        />
                        <Route
                            path={ForgotPasswordPage.path}
                            element={<ForgotPasswordPage />}
                        />
                        <Route
                            path={ChangeTempPasswordPage.path}
                            element={<ChangeTempPasswordPage />}
                        />
                    </CustomRoutes>

                    {/* Full-width pages are listed in layout/fullWidthRoutes.ts —
                        keep it in sync when adding a route here. */}
                    <CustomRoutes>
                        <Route path="/no-access" element={<NoAccessPage />} />
                        <Route
                            path="/markets/*"
                            element={
                                <RequireAccess>
                                    <MarketsView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/markets/trader/:address"
                            element={
                                <RequireAccess>
                                    <TraderView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/monitor/*"
                            element={
                                <RequireAccess>
                                    <MonitorView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/portfolio"
                            element={
                                <RequireAccess>
                                    <PortfolioView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/watchlists"
                            element={
                                <RequireAccess>
                                    <WatchlistView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/gates"
                            element={
                                <RequireAccess>
                                    <GatesView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/screener"
                            element={
                                <RequireAccess>
                                    <ConvictionScreenerView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/screener_simulation"
                            element={
                                <RequireAccess>
                                    <SimulationView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/chats"
                            element={
                                <RequireAccess>
                                    <ConversationListView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/chats/new"
                            element={
                                <RequireAccess>
                                    <NewConversationView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/chat/:conversationId"
                            element={
                                <RequireAccess>
                                    <ConversationView />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="*"
                            element={<Navigate to="/markets" replace />}
                        />
                    </CustomRoutes>

                    <CustomRoutes>
                        <Route
                            path={SettingsPage.path}
                            element={
                                <RequireAccess>
                                    <SettingsPage />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/settings/notifications"
                            element={
                                <RequireAccess>
                                    <SettingsNotifications />
                                </RequireAccess>
                            }
                        />
                    </CustomRoutes>
                    <CustomRoutes>
                        <Route
                            path="/admin/users"
                            element={
                                <RequireAccess>
                                    <AdminUsers />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/admin/users/:id"
                            element={
                                <RequireAccess>
                                    <UsersEdit />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/admin/subscribers"
                            element={
                                <RequireAccess>
                                    <SubscribersList />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/admin/audit"
                            element={
                                <RequireAccess>
                                    <AuditList />
                                </RequireAccess>
                            }
                        />
                        <Route
                            path="/admin/gates"
                            element={
                                <RequireAccess>
                                    <Navigate
                                        to="/gates?view=operators"
                                        replace
                                    />
                                </RequireAccess>
                            }
                        />
                    </CustomRoutes>
                    <Resource name="funds" {...funds} />
                    <Resource name="transactions" {...transactions} />
                    <Resource name="insights" {...insights} />
                    <Resource
                        name="users"
                        recordRepresentation={users.recordRepresentation}
                    />
                    <Resource name="user_permissions" />
                    <Resource name="newsletter_subscribers" />
                    <Resource name="admin_audit" />
                </Admin>
            </BrowserRouter>
        </ConfigurationProvider>
    );
};
